// frontend/src/pages/Analyze.jsx
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

// ── Constants ──────────────────────────────────────────────────────────────────

const COMMUNICATION_TYPES = [
    { value: 'workplace', label: 'Workplace & HR' },
    { value: 'legal', label: 'Legal & Disputes' },
    { value: 'regulatory', label: 'Regulatory & Government' },
    { value: 'commercial', label: 'Business & Commercial' },
    { value: 'professional', label: 'Professional Services' },
    { value: 'consumer', label: 'Consumer & Customer' },
    { value: 'property', label: 'Property & Housing' },
    { value: 'trades', label: 'Trades & Contractors' },
    { value: 'domestic', label: 'Domestic & Personal' },
]

const WRITER_POSITIONS_BY_TYPE = {
    workplace: [
        { value: 'superior', label: 'To someone junior or subordinate' },
        { value: 'subordinate', label: 'To someone senior or above' },
        { value: 'peer', label: 'To a colleague (equal level)' },
        { value: 'adversarial', label: 'Formal workplace dispute' },
    ],
    legal: [
        { value: 'authority', label: 'To a court or formal authority' },
        { value: 'client', label: 'As the legal professional to client' },
        { value: 'customer', label: 'As the individual in the dispute' },
        { value: 'adversarial', label: 'Formal legal opposition' },
    ],
    regulatory: [
        { value: 'subordinate', label: 'Responding to a regulator or authority' },
        { value: 'authority', label: 'As the regulatory body' },
        { value: 'customer', label: 'Consumer complaint to regulator' },
        { value: 'adversarial', label: 'Formal regulatory dispute' },
    ],
    commercial: [
        { value: 'superior', label: 'As the senior commercial party' },
        { value: 'subordinate', label: 'As the junior commercial party' },
        { value: 'peer', label: 'Equal commercial parties (B2B)' },
        { value: 'client', label: 'As the service provider to client' },
        { value: 'customer', label: 'As the customer or buyer' },
        { value: 'adversarial', label: 'Formal commercial dispute' },
    ],
    professional: [
        { value: 'superior', label: 'Senior professional to junior' },
        { value: 'client', label: 'As the professional to client' },
        { value: 'customer', label: 'Client with a complaint' },
    ],
    consumer: [
        { value: 'authority', label: 'To an ombudsman or regulator' },
        { value: 'customer', label: 'As the consumer to a business' },
        { value: 'adversarial', label: 'Formal consumer dispute' },
    ],
    property: [
        { value: 'superior', label: 'Landlord to tenant' },
        { value: 'subordinate', label: 'Tenant to landlord' },
        { value: 'peer', label: 'Equal property parties' },
        { value: 'authority', label: 'To council, tribunal or planning authority' },
        { value: 'client', label: 'Property professional to client' },
        { value: 'customer', label: 'Tenant or buyer as consumer' },
        { value: 'adversarial', label: 'Formal property dispute' },
    ],
    trades: [
        { value: 'superior', label: 'Contractor asserting authority' },
        { value: 'subordinate', label: 'Subcontractor to main contractor' },
        { value: 'client', label: 'Contractor to client' },
        { value: 'customer', label: 'Client disputing work' },
        { value: 'adversarial', label: 'Formal trades dispute' },
    ],
    domestic: [
        { value: 'peer', label: 'To a neighbour' },
        { value: 'authority', label: 'To a council or parking authority' },
        { value: 'adversarial', label: 'Formal domestic dispute' },
    ],
}

const COMMUNICATION_TYPE_LABEL = Object.fromEntries(
    COMMUNICATION_TYPES.map((t) => [t.value, t.label])
)

const WRITER_POSITION_LABELS = {
    superior: 'Writing to someone junior',
    subordinate: 'Writing to someone senior',
    peer: 'Writing to an equal',
    authority: 'Writing to a formal authority',
    client: 'As the service provider',
    customer: 'As the customer',
    adversarial: 'Formal dispute',
}

const TIER_CONFIG = {
    low: { color: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/5', bar: 'bg-green-400', label: 'Low Risk' },
    moderate: { color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/5', bar: 'bg-yellow-400', label: 'Moderate Risk' },
    elevated: { color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/5', bar: 'bg-orange-400', label: 'Elevated Risk' },
    critical: { color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/5', bar: 'bg-red-400', label: 'Critical Risk' },
}

const PHASE = {
    IDLE: 'idle',
    ANALYSING: 'analysing',
    DONE: 'done',
    REWRITE_LOADING: 'rewrite_loading',
    REWRITE_DONE: 'rewrite_done',
}

// ── 50 Analysis Status Messages ──
// A handful of these were softened from earlier wording that named internal
// architecture terms almost verbatim (weights, multipliers, engine rules,
// calibration cases, impermissibility tiers) — those read as if the UI were
// reporting a precise live backend stage, which it isn't: this list is
// timer-driven, not tied to real backend progress (see startAnalysisProgress
// below). The replacements describe the same kind of work in plainer terms
// without exposing scoring mechanics or claiming a literal tracked stage.
const ANALYSIS_STATUS_MESSAGES = [
    'Initialising analysis engine…',
    'Parsing message structure and syntax…',
    'Analysing sentence boundaries and paragraph flow…',
    'Identifying named entities and key parties…',
    'Extracting factual claims and assertions…',
    'Detecting relationship context and power dynamics…',
    'Assessing professional boundaries and hierarchies…',
    'Evaluating communication intent and purpose…',
    'Identifying potential legal exposures and liabilities…',
    'Scanning for regulatory compliance issues…',
    'Analysing contractual language and obligations…',
    'Detecting financial claims and demands…',
    'Evaluating tone, professional boundary, and sentiment…',
    'Measuring emotional intensity and language register…',
    'Identifying passive-aggressive and defensive language…',
    'Checking for escalation triggers and liability risk…',
    'Detecting ultimatum structures and deadlines…',
    'Analysing procedural fairness and transparency…',
    'Reviewing threat and coercion patterns…',
    'Cross-referencing risk patterns and flagging phrases…',
    'Weighing severity against similar real-world cases…',
    'Evaluating context and mitigating factors…',
    'Applying professional judgment and exclusions…',
    'Scoring individual risk mechanisms…',
    'Weighing the significance of what was found…',
    'Adjusting for the relationship and setting involved…',
    'Forming an overall risk picture…',
    'Settling on an overall risk level…',
    'Generating executive summary…',
    'Building factual summary from extracted data…',
    'Structuring key facts and evidence…',
    'Identifying key amounts and financial figures…',
    'Extracting key dates and deadlines…',
    'Checking for formal legal correspondence markers…',
    'Checking what, if anything, cannot safely be reworded…',
    'Validating extraction consistency…',
    'Finalising risk assessment report…',
    'Preparing detailed risk narratives…',
    'Generating consequence pathways…',
    'Building inference chains…',
    'Constructing mechanism explanations…',
    'Synthesising expert assessment…',
    'Compiling final report structure…',
    'Formatting risk assessment output…',
    'Validating report completeness…',
    'Preparing analytics and metrics…',
    'Finalising risk report…',
    'Publishing results…',
    'Analysis complete. Preparing results…',
    'Ready to display assessment…',
]

// ── 47 Rewrite Status Messages ──
const REWRITE_STATUS_MESSAGES = [
    'Initialising rewrite engine…',
    'Reviewing message context and risk factors…',
    'Analysing flagged risk mechanisms…',
    'Identifying problematic language patterns…',
    'Mapping original message structure…',
    'Preserving underlying facts and intent…',
    'Removing volatile and escalatory phrasing…',
    'Replacing confrontational language with factual framing…',
    'Refining tone for executive presence and professionalism…',
    'Balancing assertiveness with collaborative language…',
    'Ensuring legal and procedural safeguards…',
    'Removing ultimatums and unconditional demands…',
    'Strengthening factual accuracy and clarity…',
    'Enhancing professional readability and flow…',
    'Restructuring sentences for impact…',
    'Optimising paragraph transitions…',
    'Eliminating hedging and uncertainty…',
    'Replacing emotional language with objective statements…',
    'Validating factual consistency…',
    'Checking against original intent…',
    'Ensuring risk mechanisms addressed…',
    'Preserving key dates and amounts…',
    'Retaining essential reference numbers…',
    'Maintaining appropriate professional register…',
    'Removing apology and conciliatory language…',
    'Eliminating passive-aggressive undertones…',
    'Converting demands to requests…',
    'Framing deadlines as expectations…',
    'Structuring for clarity and brevity…',
    'Enhancing professional tone and credibility…',
    'Reviewing legal exposure reduction…',
    'Verifying procedural correctness…',
    'Checking escalation risk mitigation…',
    'Removing personal attacks and blame…',
    'Reframing criticism constructively…',
    'Preserving sender authority appropriately…',
    'Balancing power dynamics…',
    'Ensuring cultural and contextual appropriateness…',
    'Validating factual integrity…',
    'Cross-checking against source material…',
    'Removing duplicated content…',
    'Streamlining for impact…',
    'Polishing final rewrite for delivery…',
    'Adding professional closure…',
    'Ensuring call to action clarity…',
    'Validating brand voice consistency…',
    'Finalising rewrite structure…',
    'Formatting for readability…',
    'Ready to deliver professional rewrite…',
]

// One automatic retry, and only when nothing has streamed yet (each retry is a
// full paid generation).
const MAX_RETRIES = 1

// ── Helpers ──────────────────────────────────────────────────────────────────

const getRecommendation = (score) => {
    if (score >= 70) {
        return {
            action: 'Do not send this message in its current form.',
            detail: 'This message contains high-risk language that could cause serious misunderstandings or legal exposure. Review the assessment below before considering any response.',
            severity: 'critical',
        }
    }
    if (score >= 40) {
        return {
            action: 'Review the assessment below before sending.',
            detail: 'This message contains potential risk elements. Consider the flagged phrases and expert assessment before deciding whether to send or rewrite.',
            severity: 'elevated',
        }
    }
    return {
        action: 'This message appears safe to send.',
        detail: 'No significant risk indicators were detected. You may send this message with confidence.',
        severity: 'low',
    }
}

const describeError = ({ status, kind = 'analysis', networkError = false, detail = null } = {}) => {
    if (networkError) {
        return "We couldn't reach the analysis service. Please check your connection and try again — your message has not been sent anywhere."
    }
    switch (status) {
        case 400:
            return kind === 'rewrite'
                ? 'We could not start the rewrite for this analysis. Please try again, or run the analysis again.'
                : 'That message could not be processed. Please check it is between 50 and 50,000 characters and try again.'
        case 401:
            return 'Your session has expired. Please sign in again to continue.'
        case 403:
            // The onboarding-agreement block returns a specific, user-safe message.
            return typeof detail === 'string' && /agreement/i.test(detail)
                ? detail
                : 'Your session has expired. Please sign in again to continue.'
        case 422:
            return 'The request was not in the expected format. Please refresh the page and try again.'
        case 429:
            return 'A lot of requests are coming through right now. Please wait a few seconds and try again.'
        case 500:
        case 502:
        case 503:
        case 504:
            return kind === 'rewrite'
                ? 'The rewrite engine is temporarily unavailable. Your analysis is safe above — please try the rewrite again in a moment.'
                : 'The analysis engine is temporarily unavailable. Please try again in a moment.'
        default:
            return kind === 'rewrite'
                ? 'We could not generate a rewrite this time. Please try again.'
                : 'We could not complete the analysis this time. Please try again.'
    }
}

// Helper to safely parse raw or fenced JSON strings without blocking
const parseJSONSafely = (rawText) => {
    if (!rawText) return null
    const unescaped = rawText.replace(/\\n/g, '\n')
    const stripFences = (t) => t.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '')
    const sliceObject = (t) => {
        const first = t.indexOf('{')
        const last = t.lastIndexOf('}')
        return first !== -1 && last > first ? t.slice(first, last + 1) : t
    }

    for (const candidate of [unescaped, rawText].flatMap((src) => {
        const stripped = stripFences(src)
        return [stripped, sliceObject(stripped)]
    })) {
        try {
            const parsed = JSON.parse(candidate)
            if (parsed) return parsed
        } catch {
            // continue candidate checks
        }
    }
    return null
}

// Reads the server's error detail (a string, or {code, message}) without ever
// throwing. Only server error text is read here, never message content.
const readErrorDetail = async (response) => {
    try {
        const body = await response.json()
        const d = body?.detail
        if (typeof d === 'string') return d
        if (d && typeof d === 'object') return d.message || d.code || null
    } catch {
        // not JSON
    }
    return null
}

// Yield to the browser between stream reads so the UI stays smooth, but never
// wait more than 50ms: requestAnimationFrame does not fire in a hidden tab, so
// awaiting it alone would stall stream processing until the tab is visible again.
const yieldToUi = () =>
    Promise.race([
        new Promise((resolve) => requestAnimationFrame(resolve)),
        new Promise((resolve) => setTimeout(resolve, 50)),
    ])

// Pulls the text of "rewritten_message" out of the JSON that is still arriving,
// so the rewrite can be shown as it streams. Returns '' until that field starts.
const extractPartialRewrite = (raw) => {
    const m = raw.match(/"rewritten_message"\s*:\s*"/)
    if (!m) return ''
    const s = raw.slice(m.index + m[0].length)
    let out = ''
    for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (ch === '\\') {
            const next = s[i + 1]
            if (next === undefined) break
            if (next === 'n') out += '\n'
            else if (next === 't') out += '\t'
            else if (next === 'u') {
                const hex = s.slice(i + 2, i + 6)
                if (!/^[0-9a-fA-F]{4}$/.test(hex)) break
                out += String.fromCharCode(parseInt(hex, 16))
                i += 5
                continue
            } else out += next
            i++
            continue
        }
        if (ch === '"') break
        out += ch
    }
    return out
}

// ── Loader Components ────────────────────────────────────────────────────────
//
// Analyse and Rewrite deliberately use two different visual metaphors, not a
// shared component re-skinned with different text — this is the direct fix
// for "Analyse and Rewrite don't look like the same operation."
//
// AnalysingLoader: an indeterminate arc that continuously sweeps around a
// static ring — no fill level, no number, so it never claims a specific
// progress percentage (the thing the old numbered dial got right into
// trouble for). The continuous sweeping motion is what gives back the
// "actively performing an assessment" weight the old dial had, without the
// dishonesty of a percentage. Two visual layers only (track + sweep), no
// ping/halo effects, to keep it restrained rather than busy.
//
// RewriteLoader: Rewrite already has an honest, real progress signal — the
// text streaming in live — so its indicator leans into "writing," not
// "scanning": a small sequential-dot typing indicator, the same visual
// grammar as a chat "typing…" cue, sized to sit inline just above where the
// streamed text appears rather than dominating the screen.

const AnalysingLoader = ({ message }) => (
    <div className="flex flex-col items-center justify-center my-10">
        <div className="relative flex items-center justify-center w-28 h-28 mb-8">
            {/* Static dial track */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                <circle
                    cx="50" cy="50" r="44"
                    fill="none"
                    stroke="rgba(212,168,67,0.14)"
                    strokeWidth="3"
                />
            </svg>
            {/* Continuously sweeping arc — indeterminate, never fills, never resets to zero */}
            <svg
                className="absolute inset-0 w-full h-full animate-spin"
                style={{ animationDuration: '2.4s' }}
                viewBox="0 0 100 100"
            >
                <circle
                    cx="50" cy="50" r="44"
                    fill="none"
                    stroke="#D4A843"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="70 208"
                />
            </svg>
            {/* Static centre mark */}
            <div className="relative w-14 h-14 rounded-full bg-brand-amber/10 border border-brand-amber/40 flex items-center justify-center">
                <svg className="w-6 h-6 text-brand-amber" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                        d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4" />
                </svg>
            </div>
        </div>
        <p className="font-body text-sm text-white/80 tracking-wide text-center h-6 animate-fade-in transition-all duration-300">
            {message}
        </p>
    </div>
)

const RewriteLoader = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-amber/10 border border-brand-amber/30 mb-4">
            <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-amber animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-brand-amber animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-brand-amber animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
            <span className="text-brand-amber text-xs font-body font-medium">Writing</span>
        </div>
        <p className="font-body text-sm text-white/60 tracking-wide text-center h-6 animate-fade-in transition-all duration-300">
            {message}
        </p>
    </div>
)

const ZeroRetentionNotice = () => (
    <div className="mt-6 text-center">
        <p className="text-white/30 text-xs font-body">Your data is protected. We apply zero-retention processing.</p>
        <p className="text-white/20 text-xs font-body mt-1">No content is stored or used for training.</p>
    </div>
)

// ── Main Component ────────────────────────────────────────────────────────────

export default function Analyze() {
    const navigate = useNavigate()

    const [message, setMessage] = useState('')
    const [pasteRejected, setPasteRejected] = useState(null)

    const [showContextOverride, setShowContextOverride] = useState(false)
    const [communicationType, setCommunicationType] = useState(COMMUNICATION_TYPES[0].value)
    const [writerPosition, setWriterPosition] = useState(
        WRITER_POSITIONS_BY_TYPE[COMMUNICATION_TYPES[0].value][0].value
    )
    const [lockedCommunicationType, setLockedCommunicationType] = useState(null)

    const [error, setError] = useState('')
    const [paymentRequired, setPaymentRequired] = useState(false)

    const [phase, setPhase] = useState(PHASE.IDLE)
    const [pass1, setPass1] = useState(null)
    const [pass2Parsed, setPass2Parsed] = useState(null)

    const [analysisStatusIndex, setAnalysisStatusIndex] = useState(0)
    const [rewriteStatusIndex, setRewriteStatusIndex] = useState(0)

    const [rewriteLoading, setRewriteLoading] = useState(false)
    const [rewriteError, setRewriteError] = useState('')
    const [rewriteResult, setRewriteResult] = useState(null)
    const [rewritePartial, setRewritePartial] = useState('')
    const [rewritesRemaining, setRewritesRemaining] = useState(null)
    const [criticalConfirmed, setCriticalConfirmed] = useState(false)
    const [copied, setCopied] = useState(false)

    const analysisTimerRef = useRef(null)
    const rewriteTimerRef = useRef(null)

    const MAX_CHARS = 50000
    const MIN_CHARS = 50
    const charCount = message.length
    const overLimit = charCount > MAX_CHARS
    const underMinimum = charCount > 0 && charCount < MIN_CHARS
    const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

    useEffect(() => {
        return () => {
            if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)
            if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
        }
    }, [])

    const getWriterPositionOptions = (ct) => {
        return WRITER_POSITIONS_BY_TYPE[ct] || WRITER_POSITIONS_BY_TYPE.workplace
    }

    const startAnalysisProgress = () => {
        if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)

        setAnalysisStatusIndex(0)
        // Faster, smoother progress steps
        analysisTimerRef.current = setInterval(() => {
            setAnalysisStatusIndex((prev) => (prev + 1) % ANALYSIS_STATUS_MESSAGES.length)
        }, 800)

        return analysisTimerRef.current
    }

    const startRewriteProgress = () => {
        if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)

        setRewriteStatusIndex(0)
        rewriteTimerRef.current = setInterval(() => {
            setRewriteStatusIndex((prev) => (prev + 1) % REWRITE_STATUS_MESSAGES.length)
        }, 800)

        return rewriteTimerRef.current
    }

    const handlePaste = (e) => {
        const pasted = e.clipboardData?.getData('text') ?? ''
        if (!pasted) return

        const target = e.target
        const selectionLength = target.selectionEnd - target.selectionStart
        const resulting = message.length - selectionLength + pasted.length

        if (resulting > MAX_CHARS) {
            e.preventDefault()
            setPasteRejected({
                attempted: resulting,
                excess: resulting - MAX_CHARS,
            })
            return
        }

        setPasteRejected(null)
    }

    const executeStreamAnalysis = async (requestBody) => {
        setError('')
        setPaymentRequired(false)
        setPass1(null)
        setPass2Parsed(null)
        setRewriteResult(null)
        setRewriteError('')
        setRewritesRemaining(null)
        setCriticalConfirmed(false)
        setShowContextOverride(false)
        setLockedCommunicationType(null)
        setPhase(PHASE.ANALYSING)

        startAnalysisProgress()
        const token = localStorage.getItem('gmc_token')

        try {
            const response = await fetch(`${apiBase}/analyze/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify(requestBody),
            })

            if (response.status === 402) {
                if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)
                setPaymentRequired(true)
                setPhase(PHASE.IDLE)
                return
            }

            if (!response.ok) {
                if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)
                if (response.status === 401) {
                    // Stale or invalid token: drop it so the next attempt is not
                    // rejected again, and ask the user to sign in.
                    localStorage.removeItem('gmc_token')
                }
                const detail = response.status === 403 ? await readErrorDetail(response) : null
                setError(describeError({ status: response.status, kind: 'analysis', detail }))
                setPhase(PHASE.IDLE)
                return
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ''
            let pass2Raw = ''
            let receivedPass1 = false
            let streamError = ''
            let currentEvent = ''

            while (true) {
                const { value, done } = await reader.read()
                if (done) break

                buffer += decoder.decode(value, { stream: true })

                // Non-blocking frame processing to eliminate main thread UI stutter
                await yieldToUi()

                const lines = buffer.split('\n')
                buffer = lines.pop()

                for (const rawLine of lines) {
                    const line = rawLine.replace(/\r$/, '')

                    if (line.startsWith('event:')) {
                        currentEvent = line.slice(6).trim()
                        continue
                    }
                    if (!line.startsWith('data:')) continue

                    const data = line.slice(5).replace(/^ /, '')

                    if (currentEvent === 'pass1') {
                        try {
                            const parsed = JSON.parse(data)
                            setPass1(parsed)
                            receivedPass1 = true

                            // INSTANT RENDER: Transition out of blocking loader as soon as Pass 1 is ready
                            setPhase(PHASE.DONE)

                            if (parsed.fallback_triggered) {
                                setShowContextOverride(true)
                                setLockedCommunicationType(null)
                            } else if (parsed.partial_fallback) {
                                setShowContextOverride(true)
                                if (parsed.communication_type) {
                                    setLockedCommunicationType(parsed.communication_type)
                                    setCommunicationType(parsed.communication_type)
                                }
                            }
                        } catch (err) {
                            console.error('Could not parse pass1 payload:', err)
                        }

                    } else if (currentEvent === 'pass2_stream') {
                        // Dynamically jump progress text forward if user is still watching loader
                        setAnalysisStatusIndex((prev) => Math.max(prev, 35))

                        try {
                            const parsed = JSON.parse(data)
                            const chunkText = parsed.chunk || data
                            pass2Raw += chunkText
                        } catch {
                            pass2Raw += data
                        }

                        // Stream updates incrementally to UI as Pass 2 accumulates
                        const incrementalPass2 = parseJSONSafely(pass2Raw)
                        if (incrementalPass2?.mechanism_narratives || incrementalPass2?.inference_chain) {
                            setPass2Parsed(incrementalPass2)
                        }

                    } else if (currentEvent === 'done') {
                        if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)

                    } else if (currentEvent === 'error') {
                        try {
                            const parsed = JSON.parse(data)
                            streamError = parsed.error || 'analysis_failed'
                        } catch {
                            streamError = 'analysis_failed'
                        }
                    }
                }
            }

            if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)

            if (streamError) {
                setError(describeError({ kind: 'analysis' }))
                setPhase(PHASE.IDLE)
                return
            }

            if (!receivedPass1) {
                setError('The analysis came back empty. Please try again.')
                setPhase(PHASE.IDLE)
                return
            }

            // Final fallback parse check for Pass 2
            if (pass2Raw) {
                const finalPass2 = parseJSONSafely(pass2Raw)
                if (finalPass2) {
                    setPass2Parsed(finalPass2)
                }
            }

            setPhase(PHASE.DONE)

        } catch {
            if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)
            setError(describeError({ kind: 'analysis', networkError: true }))
            setPhase(PHASE.IDLE)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        await executeStreamAnalysis({
            message,
            rewrite_requested: false,
        })
    }

    const handleReanalyseWithContext = async () => {
        await executeStreamAnalysis({
            message,
            communication_type: communicationType,
            writer_position: writerPosition,
            rewrite_requested: false,
        })
    }

    const handleGenerateRewrite = async (retryCount = 0) => {
        if (!pass1) return

        if (pass1.tier?.toLowerCase() === 'critical' && !criticalConfirmed) return

        setRewriteLoading(true)
        setRewriteError('')
        setRewriteResult(null)
        setRewritesRemaining(null)
        setRewritePartial('')
        setCopied(false)

        startRewriteProgress()
        const token = localStorage.getItem('gmc_token')
        let retrying = false
        let streamStarted = false

        try {
            const response = await fetch(`${apiBase}/analyze/rewrite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify({
                    pass1_output: pass1,
                    critical_confirmed: criticalConfirmed,
                }),
            })

            if (response.status === 402) {
                if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
                setRewriteError('Your rewrite allowance has been used. Pay £1.99 or upgrade your plan to generate a rewrite.')
                setRewriteLoading(false)
                return
            }

            if (response.status === 401) {
                if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
                localStorage.removeItem('gmc_token')
                setRewriteError(describeError({ status: 401, kind: 'rewrite' }))
                setRewriteLoading(false)
                return
            }

            if (response.status === 422) {
                if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
                const errorData = await response.json().catch(() => null)
                if (errorData) console.error('Validation error:', errorData)
                setRewriteError(describeError({ status: 422, kind: 'rewrite' }))
                setRewriteLoading(false)
                return
            }

            if (!response.ok) {
                if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
                if ([500, 502, 503, 504].includes(response.status) && retryCount < MAX_RETRIES) {
                    const delay = 2000 * (retryCount + 1)
                    retrying = true
                    setRewriteError(`Retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`)
                    setTimeout(() => {
                        handleGenerateRewrite(retryCount + 1)
                    }, delay)
                    return
                }
                const detail = response.status === 403 ? await readErrorDetail(response) : null
                setRewriteError(describeError({ status: response.status, kind: 'rewrite', detail }))
                setRewriteLoading(false)
                return
            }

            streamStarted = true
            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ''
            let rewriteMessage = ''
            let removedElement = ''
            let intentPreserved = ''
            let rawResponse = ''
            let currentEvent = ''

            while (true) {
                const { value, done } = await reader.read()
                if (done) break

                buffer += decoder.decode(value, { stream: true })

                await yieldToUi()

                const lines = buffer.split('\n')
                buffer = lines.pop()

                for (const rawLine of lines) {
                    const line = rawLine.replace(/\r$/, '')
                    if (line.startsWith('event:')) {
                        currentEvent = line.slice(6).trim()
                    } else if (line.startsWith('data:')) {
                        const data = line.slice(5).replace(/^ /, '')

                        if (currentEvent === 'rewrite_stream') {
                            try {
                                const parsed = JSON.parse(data)
                                const chunkText = parsed.chunk || data
                                rawResponse += chunkText
                                rewriteMessage += chunkText.replace(/\\n/g, '\n')
                            } catch {
                                rawResponse += data
                                rewriteMessage += data.replace(/\\n/g, '\n')
                            }
                        } else if (currentEvent === 'done') {
                            if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
                            try {
                                const doneData = JSON.parse(data.trim())
                                if (doneData.rewrites_remaining !== undefined) {
                                    setRewritesRemaining(doneData.rewrites_remaining)
                                }
                            } catch {
                                // ignore
                            }
                        } else if (currentEvent === 'error') {
                            if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
                            setRewriteError('The rewrite engine reported an error partway through. Please try again.')
                            setRewriteLoading(false)
                            return
                        }
                    }
                }

                // Show the rewrite text as it arrives instead of waiting for the end
                setRewritePartial(extractPartialRewrite(rawResponse))
            }

            if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)

            let parsedOk = false
            const parsedData = parseJSONSafely(rawResponse)
            if (parsedData) {
                const rewriteData = parsedData?.rewrite || parsedData
                if (rewriteData?.rewritten_message) {
                    rewriteMessage = rewriteData.rewritten_message
                    removedElement = rewriteData.removed_element || ''
                    intentPreserved = rewriteData.intent_preserved || ''
                    parsedOk = true
                }
            }

            if (!parsedOk) {
                const m = rawResponse.match(/"rewritten_message"\s*:\s*"((?:[^"\\]|\\.)*)"/)
                if (m) {
                    try {
                        rewriteMessage = JSON.parse(`"${m[1]}"`)
                        parsedOk = true
                    } catch {
                        // ignore
                    }
                }
            }

            if (!parsedOk) {
                console.error('Rewrite parse failed. Raw payload:', rawResponse.slice(0, 400))
                setRewriteError('We could not read the rewrite that came back. Please try again.')
                setRewriteLoading(false)
                return
            }

            if (rewriteMessage) {
                setRewriteResult({
                    rewritten_message: rewriteMessage,
                    removed_element: removedElement,
                    intent_preserved: intentPreserved,
                })
                setPhase(PHASE.REWRITE_DONE)
            } else {
                setRewriteError('No rewrite was returned. Please try again.')
            }

        } catch (err) {
            console.error('Rewrite error:', err)
            if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)
            // Retry only if nothing had started streaming (no wasted generation)
            if (!streamStarted && retryCount < MAX_RETRIES) {
                const delay = 2000 * (retryCount + 1)
                retrying = true
                setRewriteError(`Retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`)
                setTimeout(() => {
                    handleGenerateRewrite(retryCount + 1)
                }, delay)
            } else {
                setRewriteError(describeError({ kind: 'rewrite', networkError: true }))
            }
        } finally {
            if (!retrying) {
                setRewriteLoading(false)
            }
        }
    }

    const handleReset = () => {
        if (analysisTimerRef.current) clearInterval(analysisTimerRef.current)
        if (rewriteTimerRef.current) clearInterval(rewriteTimerRef.current)

        setMessage('')
        setPhase(PHASE.IDLE)
        setPass1(null)
        setPass2Parsed(null)
        setRewriteResult(null)
        setRewriteError('')
        setRewritesRemaining(null)
        setError('')
        setPaymentRequired(false)
        setAnalysisStatusIndex(0)
        setRewriteStatusIndex(0)
        setRewritePartial('')
        setCriticalConfirmed(false)
        setShowContextOverride(false)
        setLockedCommunicationType(null)
    }

    const handleCopyRewrite = async () => {
        const text = rewriteResult?.rewritten_message
        if (!text) return

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text)
            } else {
                const ta = document.createElement('textarea')
                ta.value = text
                ta.style.position = 'fixed'
                ta.style.opacity = '0'
                document.body.appendChild(ta)
                ta.focus()
                ta.select()
                document.execCommand('copy')
                document.body.removeChild(ta)
            }
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Copy failed:', err)
        }
    }

    const tier = pass1?.tier?.toLowerCase() || 'low'
    const config = TIER_CONFIG[tier] || TIER_CONFIG.low
    const score = pass1?.score ?? 0
    const recommendation = pass1 ? getRecommendation(score) : null

    return (
        <div className="min-h-screen bg-[#0D1117] flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">
                {/* ── Input Form ── */}
                {phase === PHASE.IDLE && (
                    <>
                        <div className="mb-10">
                            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
                                Analyse your message
                            </h1>
                            <p className="text-white/50 font-body text-sm">
                                Paste the message you want to check. It will never be stored.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-white/70 text-xs font-medium uppercase tracking-wider block">
                                        Message
                                    </label>
                                    <span className={`text-xs font-body tabular-nums ${overLimit
                                        ? 'text-red-400 font-medium'
                                        : charCount > MAX_CHARS * 0.9
                                            ? 'text-brand-amber'
                                            : 'text-white/40'
                                        }`}>
                                        {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
                                    </span>
                                </div>
                                <textarea
                                    value={message}
                                    onChange={(e) => {
                                        setMessage(e.target.value)
                                        if (pasteRejected) setPasteRejected(null)
                                    }}
                                    onPaste={handlePaste}
                                    placeholder="Paste the message you want to analyse here…"
                                    rows={10}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-brand-amber/40 focus:outline-none transition-colors resize-none font-body text-sm leading-relaxed"
                                    style={{ fontFamily: 'inherit' }}
                                    required
                                />

                                {pasteRejected && (
                                    <div className="mt-3 border border-red-500/30 bg-red-500/[0.06] rounded-xl px-4 py-3.5">
                                        <p className="text-red-300/90 text-sm font-body font-medium mb-1">
                                            That paste was not added — it is too long.
                                        </p>
                                        <p className="text-white/60 text-sm font-body leading-relaxed">
                                            It would have come to {pasteRejected.attempted.toLocaleString()} characters,
                                            which is {pasteRejected.excess.toLocaleString()} over the {MAX_CHARS.toLocaleString()} limit.
                                            Nothing has been added to the box, so your text is still on your clipboard.
                                            Please shorten it and paste again — GMC analyses one message at a time, and
                                            a partial message would produce a misleading result.
                                        </p>
                                    </div>
                                )}

                                {overLimit && !pasteRejected && (
                                    <div className="mt-3 border border-red-500/30 bg-red-500/[0.06] rounded-xl px-4 py-3">
                                        <p className="text-red-300/90 text-sm font-body">
                                            This message is {(charCount - MAX_CHARS).toLocaleString()} characters over the
                                            limit. Please shorten it before analysing.
                                        </p>
                                    </div>
                                )}

                                {underMinimum && (
                                    <div className="mt-3 border border-white/10 bg-white/[0.02] rounded-xl px-4 py-3">
                                        <p className="text-white/50 text-sm font-body">
                                            A little more is needed — {MIN_CHARS} characters minimum,
                                            {' '}{MIN_CHARS - charCount} to go.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {paymentRequired && (
                                <div className="border border-brand-amber/40 bg-brand-amber/5 rounded-xl p-5">
                                    <div className="flex items-start gap-3">
                                        <div className="w-5 h-5 rounded border border-brand-amber flex items-center justify-center shrink-0 mt-0.5">
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                <path d="M5 1v4M5 8v.5" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-brand-amber text-sm font-body font-medium mb-1">
                                                Payment required to continue
                                            </p>
                                            <p className="text-white/50 text-sm font-body mb-4">
                                                Your free analyses have been used. Pay £1.99 for this analysis, or subscribe to a plan for volume pricing.
                                            </p>
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/pay', { state: { message } })}
                                                    className="px-5 py-2.5 bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] text-xs font-bold rounded-xl transition-all shadow-sm"
                                                >
                                                    Pay £1.99 to continue
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/plans')}
                                                    className="px-5 py-2.5 border border-white/10 text-white/70 hover:text-white text-xs font-bold rounded-xl transition-all"
                                                >
                                                    View plans
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="border border-red-500/30 bg-red-500/10 rounded-xl px-4 py-3">
                                    <div className="flex items-start gap-2">
                                        <svg className="w-4 h-4 text-red-400 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="none">
                                            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
                                            <path d="M10 6v4M10 13v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                        <p className="text-red-400 text-sm font-body">{error}</p>
                                    </div>
                                </div>
                            )}

                            {!paymentRequired && (
                                <button
                                    type="submit"
                                    disabled={!message.trim() || overLimit || underMinimum}
                                    className="w-full px-6 py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 text-[#0D1117]
                                        bg-emerald-500 hover:bg-emerald-400
                                        shadow-[0_4px_20px_-4px_rgba(16,185,129,0.5)]
                                        hover:ring-2 hover:ring-inset hover:ring-amber-400
                                        hover:shadow-[0_6px_26px_-4px_rgba(52,211,153,0.6)]
                                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1117]
                                        disabled:bg-amber-400 disabled:text-[#0D1117]/75 disabled:cursor-not-allowed
                                        disabled:ring-0 disabled:hover:ring-0
                                        disabled:shadow-[0_4px_18px_-6px_rgba(251,191,36,0.45)] disabled:hover:shadow-[0_4px_18px_-6px_rgba(251,191,36,0.45)]"
                                >
                                    Analyse message
                                </button>
                            )}
                        </form>

                        <div className="mt-6 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                            <p className="text-white/30 text-xs font-body">
                                Zero retention — your message is not stored at any point during analysis
                            </p>
                        </div>
                    </>
                )}

                {/* ── Ambient Analysing Screen ── */}
                {phase === PHASE.ANALYSING && (
                    <div className="flex flex-col items-center py-12">
                        <div className="text-center mb-4">
                            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                                Analysing Your Message
                            </h2>
                            <p className="text-white/50 font-body text-sm">
                                Our AI engine is performing a comprehensive risk analysis
                            </p>
                        </div>

                        <AnalysingLoader message={ANALYSIS_STATUS_MESSAGES[analysisStatusIndex]} />
                        <ZeroRetentionNotice />
                    </div>
                )}

                {/* ── Results ── */}
                {(phase === PHASE.DONE || phase === PHASE.REWRITE_DONE) && pass1 && (
                    <>
                        <div className="mb-8">
                            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
                                Analysis complete
                            </h1>
                            <p className="text-white/50 font-body text-sm">
                                {phase === PHASE.REWRITE_DONE ? (
                                    <>Your professional rewrite is ready below, alongside the full report.</>
                                ) : (
                                    <>Full report below. Click <span className="text-brand-amber font-medium">{tier === 'low' || tier === 'moderate' ? 'Polish & Improve Wording' : 'Generate Professional Rewrite'}</span> for a polished version.</>
                                )}
                            </p>
                        </div>

                        {/* Inferred Context Chip */}
                        {pass1?.communication_type && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-white/30 text-xs font-body">Context identified:</span>
                                    <span className="inline-flex items-center gap-1.5 border border-brand-amber/30 bg-brand-amber/5 text-brand-amber text-xs font-body px-3 py-1 rounded-full">
                                        {COMMUNICATION_TYPE_LABEL[pass1.communication_type] || pass1.communication_type}
                                        {pass1.writer_position && (
                                            <span className="text-brand-amber/60">
                                                — {WRITER_POSITION_LABELS[pass1.writer_position] || pass1.writer_position}
                                            </span>
                                        )}
                                    </span>
                                    {pass1.context_confidence != null && pass1.context_confidence < 0.75 && (
                                        <span className="text-white/30 text-xs font-body">
                                            (low confidence)
                                        </span>
                                    )}
                                    {pass1.fallback_triggered && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowContextOverride(true)
                                                setLockedCommunicationType(null)
                                            }}
                                            className="text-white/40 hover:text-brand-amber text-xs font-body underline transition-colors ml-2"
                                        >
                                            Set context manually
                                        </button>
                                    )}
                                    {pass1.partial_fallback && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowContextOverride(true)
                                                if (pass1.communication_type) {
                                                    setLockedCommunicationType(pass1.communication_type)
                                                    setCommunicationType(pass1.communication_type)
                                                }
                                            }}
                                            className="text-white/40 hover:text-brand-amber text-xs font-body underline transition-colors ml-2"
                                        >
                                            Set writer position
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Context Override Box */}
                        {showContextOverride && (
                            <div className="border border-brand-amber/30 bg-brand-amber/5 rounded-xl p-6 mb-6">
                                <p className="text-brand-amber/80 text-xs font-body mb-3">
                                    {lockedCommunicationType
                                        ? 'We need to know who you are in relation to the recipient. Please select your position below.'
                                        : 'The message was too ambiguous to classify. Please select the context below.'
                                    }
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                    {!lockedCommunicationType ? (
                                        <select
                                            value={communicationType}
                                            onChange={(e) => {
                                                setCommunicationType(e.target.value)
                                                setWriterPosition(
                                                    WRITER_POSITIONS_BY_TYPE[e.target.value][0].value
                                                )
                                            }}
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-brand-amber/40 focus:outline-none transition-colors font-body text-sm"
                                        >
                                            {COMMUNICATION_TYPES.map((type) => (
                                                <option key={type.value} value={type.value} className="bg-[#0D1117]">
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="w-full px-4 py-3 bg-white/5 border border-brand-amber/20 rounded-xl text-white/60 text-sm font-body flex items-center">
                                            <span className="text-brand-amber/80 mr-2">🔒</span>
                                            {COMMUNICATION_TYPE_LABEL[lockedCommunicationType] || lockedCommunicationType}
                                            <span className="text-white/30 text-xs ml-2">(locked)</span>
                                        </div>
                                    )}
                                    <select
                                        value={writerPosition}
                                        onChange={(e) => setWriterPosition(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-brand-amber/40 focus:outline-none transition-colors font-body text-sm"
                                    >
                                        {getWriterPositionOptions(
                                            lockedCommunicationType || communicationType
                                        ).map((pos) => (
                                            <option key={pos.value} value={pos.value} className="bg-[#0D1117]">
                                                {pos.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={handleReanalyseWithContext}
                                    className="px-5 py-2 bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] text-xs font-bold rounded-xl transition-all"
                                >
                                    Re-analyse with selected context
                                </button>
                            </div>
                        )}

                        {/* Executive Summary */}
                        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                            <h2 className="font-display text-base font-semibold text-white mb-3">
                                Executive Summary
                            </h2>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                <div>
                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                        Risk Tier
                                    </p>
                                    <p className={`font-display text-xl font-bold ${config.color}`}>
                                        {config.label}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                        Risk Score
                                    </p>
                                    <p className={`font-display text-xl font-bold ${config.color}`}>
                                        {score}/100
                                    </p>
                                </div>
                                <div>
                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                        Flagged Phrases
                                    </p>
                                    <p className="font-display text-xl font-bold text-white">
                                        {pass1.triggered_phrases?.length || 0}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Recommendation Box */}
                        {recommendation && (
                            <div className={`bg-white/5 border ${recommendation.severity === 'critical' ? 'border-red-500/30' :
                                recommendation.severity === 'elevated' ? 'border-orange-500/30' :
                                    'border-green-500/30'
                                } rounded-xl p-6 mb-6`}>
                                <h3 className="font-display text-base font-semibold text-white mb-2">
                                    Recommended Action
                                </h3>
                                <p className={`font-body text-sm font-medium ${recommendation.severity === 'critical' ? 'text-red-400' :
                                    recommendation.severity === 'elevated' ? 'text-orange-400' :
                                        'text-green-400'
                                    }`}>
                                    {recommendation.action}
                                </p>
                                <p className="text-white/50 font-body text-sm mt-2">
                                    {recommendation.detail}
                                </p>
                                {recommendation.severity !== 'low' && (
                                    <p className="text-white/40 text-xs font-body mt-3">
                                        Review the assessment below before generating a rewrite.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Risk Score Banner */}
                        <div className={`border ${config.border} ${config.bg} rounded-xl p-6 mb-6`}>
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                        Risk assessment
                                    </p>
                                    <p className={`font-display text-3xl font-bold ${config.color}`}>
                                        {config.label}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                        Score
                                    </p>
                                    <p className={`font-display text-5xl font-bold ${config.color}`}>
                                        {score}
                                    </p>
                                </div>
                            </div>
                            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all duration-700 ${config.bar}`} style={{ width: `${score}%` }} />
                            </div>
                        </div>

                        {/* Active Flags */}
                        {pass1.flags && (
                            <div className="flex flex-wrap gap-2 mb-6">
                                {Object.entries(pass1.flags).map(([flag, active]) =>
                                    active && (
                                        <span key={flag} className="border border-white/10 bg-white/5 text-white/60 text-xs font-body px-3 py-1 rounded-full">
                                            {flag.replace(/_/g, ' ')}
                                        </span>
                                    )
                                )}
                            </div>
                        )}

                        {/* Triggered Phrases */}
                        {pass1.triggered_phrases?.length > 0 && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                                <h2 className="font-display text-lg font-semibold text-white mb-4">
                                    Flagged phrases
                                </h2>
                                <div className="space-y-4">
                                    {pass1.triggered_phrases.map((phrase, i) => (
                                        <div key={i} className="border-b border-white/10 pb-4 last:border-0 last:pb-0">
                                            <p className="text-white text-sm font-body font-medium mb-1">
                                                "{phrase.phrase}"
                                            </p>
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="text-white/50 text-xs font-body">
                                                    {phrase.mechanism?.replace(/_/g, ' ')}
                                                </span>
                                                <span className={`text-xs font-body font-medium ${phrase.severity === 'critical' ? 'text-red-400' :
                                                    phrase.severity === 'elevated' ? 'text-orange-400' :
                                                        'text-yellow-400'
                                                    }`}>
                                                    {phrase.severity}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Expert Assessment (Streams in live or renders on arrival) */}
                        {pass2Parsed ? (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                                <h2 className="font-display text-lg font-semibold text-white mb-4 flex items-center justify-between">
                                    <span>Expert assessment</span>
                                    {!pass2Parsed.inference_chain && (
                                        <span className="text-xs text-brand-amber/70 font-normal animate-pulse">
                                            Generating detailed narrative…
                                        </span>
                                    )}
                                </h2>

                                <div className="space-y-8">
                                    {pass2Parsed.mechanism_narratives?.map((item, i) => (
                                        <div key={i} className="border-b border-white/10 pb-6 last:border-0 last:pb-0">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="text-brand-amber text-xs font-body uppercase tracking-widest">
                                                    {item.mechanism?.replace(/_/g, ' ')}
                                                </span>
                                                <span className={`text-[10px] font-body font-medium px-2 py-0.5 rounded ${config.bg} ${config.color}`}>
                                                    Risk factor
                                                </span>
                                            </div>
                                            <p className="text-white font-body text-sm leading-relaxed mb-3">
                                                {item.narrative}
                                            </p>
                                            {item.consequence && (
                                                <div className={`border-l-2 ${config.border} pl-4 py-1`}>
                                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                                        Likely consequence
                                                    </p>
                                                    <p className="text-white/50 font-body text-sm leading-relaxed">
                                                        {item.consequence}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Inference Chain */}
                                    {pass2Parsed.inference_chain?.steps?.length > 0 && (
                                        <div className="border-t border-white/10 pt-6">
                                            <h3 className="font-display text-base font-semibold text-white mb-4">
                                                Risk pathway
                                            </h3>
                                            <div className="space-y-4 mb-4">
                                                {pass2Parsed.inference_chain.steps.map((step, i) => (
                                                    <div key={i} className="flex items-start gap-3">
                                                        <div className={`w-6 h-6 rounded-full border ${config.border} flex items-center justify-center shrink-0 mt-0.5`}>
                                                            <span className={`text-xs font-body font-bold ${config.color}`}>
                                                                {i + 1}
                                                            </span>
                                                        </div>
                                                        <p className="text-white/50 font-body text-sm leading-relaxed">
                                                            {step}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                            {pass2Parsed.inference_chain.summary && (
                                                <div className="border-t border-white/10 pt-4 mt-4">
                                                    <p className={`font-body text-sm font-medium ${config.color}`}>
                                                        {pass2Parsed.inference_chain.summary}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6 flex items-center gap-3">
                                <div className="w-4 h-4 rounded-full border-2 border-brand-amber border-t-transparent animate-spin shrink-0" />
                                <p className="text-white/60 text-sm font-body">
                                    Synthesising expert narratives and consequence pathways…
                                </p>
                            </div>
                        )}

                        {/* Safety Gateway Advisory Panel */}
                        {pass1?.safety_gateway_triggered && pass2Parsed?.advisory_note && (
                            <div className="bg-red-500/[0.06] border border-red-500/30 rounded-xl p-6 mb-6">
                                <div className="flex items-start gap-3 mb-4">
                                    <div className="w-5 h-5 rounded border border-red-500/60 flex items-center justify-center shrink-0 mt-0.5">
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                            <path d="M5 2v3M5 7v.5" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-red-300 text-sm font-body font-semibold mb-1">
                                            This message cannot be rewritten
                                        </p>
                                        <p className="text-white/60 text-sm font-body leading-relaxed">
                                            {pass2Parsed.advisory_note}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Rewrite Section — shown on all tiers, hidden when safety gateway fired */}
                        {!pass1?.safety_gateway_triggered && (
                            <div className="bg-white/5 border border-brand-amber/40 rounded-xl p-6 mb-6">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <div>
                                        <h3 className="font-display text-base font-semibold text-white">
                                            {phase === PHASE.REWRITE_DONE
                                                ? (tier === 'low' || tier === 'moderate' ? 'Your polished message' : 'Your professional rewrite')
                                                : rewriteLoading
                                                    ? (tier === 'low' || tier === 'moderate' ? 'Polishing your wording…' : 'Generating your professional rewrite…')
                                                    : (tier === 'low' || tier === 'moderate' ? 'Want to polish the wording?' : 'Need a professional rewrite?')
                                            }
                                        </h3>
                                        <p className="text-white/50 text-sm font-body">
                                            {phase === PHASE.REWRITE_DONE
                                                ? (tier === 'low' || tier === 'moderate'
                                                    ? 'An improved version of your message — same intent, clearer wording.'
                                                    : 'A refined version of your message, based on the analysis.')
                                                : (tier === 'low' || tier === 'moderate'
                                                    ? 'GMC can improve the clarity and professionalism of your message. This does not imply anything is wrong with it.'
                                                    : 'Generate a refined version of your message based on the analysis.')
                                            }
                                        </p>
                                        {rewritesRemaining !== null && rewritesRemaining >= 0 && (
                                            <p className="text-white/40 text-xs font-body mt-1">
                                                {rewritesRemaining} rewrites remaining
                                            </p>
                                        )}
                                    </div>
                                    {!rewriteLoading && !rewriteResult && !rewriteError && (
                                        <button
                                            onClick={() => handleGenerateRewrite(0)}
                                            disabled={tier === 'critical' && !criticalConfirmed}
                                            className="px-6 py-2.5 bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] text-xs font-bold rounded-xl transition-all shadow-sm whitespace-nowrap disabled:bg-white/10 disabled:text-white/30 disabled:cursor-not-allowed disabled:shadow-none"
                                        >
                                            {tier === 'low' || tier === 'moderate' ? 'Polish & Improve Wording' : 'Generate Professional Rewrite'}
                                        </button>
                                    )}
                                </div>

                                {/* Critical Confirmation Gate */}
                                {tier === 'critical' && !rewriteResult && !rewriteLoading && (
                                    <div className="mt-5 pt-5 border-t border-red-500/20">
                                        <div className="border border-red-500/30 bg-red-500/[0.06] rounded-xl p-5">
                                            <p className="text-red-300/90 text-sm font-body leading-relaxed mb-4">
                                                This message has been identified as carrying significant legal
                                                or procedural risk. GMC has provided a suggested rewrite, but
                                                this does not constitute legal advice. For matters involving
                                                termination, formal disciplinary action, or legal proceedings,
                                                you should consult a qualified professional before sending any
                                                communication.
                                            </p>
                                            <label className="flex items-start gap-3 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={criticalConfirmed}
                                                    onChange={(e) => setCriticalConfirmed(e.target.checked)}
                                                    className="mt-0.5 w-4 h-4 shrink-0 accent-red-500 cursor-pointer"
                                                />
                                                <span className="text-white/70 text-sm font-body leading-relaxed">
                                                    I understand and wish to view the suggested rewrite.
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {/* Ambient Rewrite Loading */}
                                {rewriteLoading && (
                                    <div className="mt-4 pt-4 border-t border-white/10">
                                        <RewriteLoader message={REWRITE_STATUS_MESSAGES[rewriteStatusIndex]} />
                                        {rewritePartial && (
                                            <div className="mt-2 bg-[#0D1117] rounded-xl p-4 border border-white/10">
                                                <p className="text-brand-amber text-xs font-body uppercase tracking-widest mb-2">
                                                    Rewrite in progress
                                                </p>
                                                <p className="text-white/80 font-body text-sm leading-relaxed whitespace-pre-line">
                                                    {rewritePartial}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Rewrite Error */}
                                {rewriteError && (
                                    <div className="border border-red-500/30 bg-red-500/10 rounded-xl px-4 py-3 mt-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-red-400 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="none">
                                                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
                                                <path d="M10 6v4M10 13v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            </svg>
                                            <div className="flex-1">
                                                <p className="text-red-400 text-sm font-body">{rewriteError}</p>
                                                <div className="flex items-center gap-4 mt-2">
                                                    <button
                                                        onClick={() => handleGenerateRewrite(0)}
                                                        className="text-brand-amber text-xs font-body font-medium underline"
                                                    >
                                                        Try again
                                                    </button>
                                                    <button
                                                        onClick={() => setRewriteError('')}
                                                        className="text-white/40 text-xs font-body underline"
                                                    >
                                                        Dismiss
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Rewrite Result */}
                                {rewriteResult?.rewritten_message && phase === PHASE.REWRITE_DONE && (
                                    <div className="mt-4 border-t border-white/10 pt-4">
                                        <div className="bg-[#0D1117] rounded-xl p-4 border border-white/10">
                                            <div className="flex items-center justify-between mb-3">
                                                <p className="text-brand-amber text-xs font-body uppercase tracking-widest">
                                                    Professional rewrite
                                                </p>
                                                <div className="flex items-center gap-3">
                                                    {rewritesRemaining !== null && rewritesRemaining >= 0 && (
                                                        <span className="text-white/40 text-xs font-body">
                                                            {rewritesRemaining} remaining
                                                        </span>
                                                    )}
                                                    <button
                                                        onClick={handleCopyRewrite}
                                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 hover:border-brand-amber/40 hover:bg-white/5 transition-all text-white/60 hover:text-white"
                                                    >
                                                        {copied ? (
                                                            <>
                                                                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                                                    <path d="M3 7.5L5.5 10L11 4" stroke="#D4A843" strokeWidth="1.6"
                                                                        strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                                <span className="text-brand-amber text-xs font-body font-medium">Copied</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                                                    <rect x="4.5" y="4.5" width="7" height="7" rx="1.5"
                                                                        stroke="currentColor" strokeWidth="1.3" />
                                                                    <path d="M2.5 9.5V3a1 1 0 011-1H9" stroke="currentColor"
                                                                        strokeWidth="1.3" strokeLinecap="round" />
                                                                </svg>
                                                                <span className="text-xs font-body">Copy</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            <p className="text-white font-body text-sm leading-relaxed whitespace-pre-line mb-3">
                                                {rewriteResult.rewritten_message}
                                            </p>

                                            {/* Original vs Rewrite Comparison */}
                                            <div className="border-t border-white/10 pt-3 mt-3">
                                                <p className="text-white/30 text-xs font-body mb-2">Original vs Rewrite</p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <div className="bg-white/5 rounded-lg p-3">
                                                        <p className="text-white/40 text-[10px] font-body uppercase tracking-widest mb-1">Original</p>
                                                        <p className="text-white/50 text-xs font-body line-clamp-3">
                                                            {message.length > 100 ? message.substring(0, 100) + '…' : message}
                                                        </p>
                                                    </div>
                                                    <div className="bg-brand-amber/5 rounded-lg p-3 border border-brand-amber/20">
                                                        <p className="text-brand-amber text-[10px] font-body uppercase tracking-widest mb-1">Rewrite</p>
                                                        <p className="text-white/70 text-xs font-body line-clamp-3">
                                                            {rewriteResult.rewritten_message.length > 100
                                                                ? rewriteResult.rewritten_message.substring(0, 100) + '…'
                                                                : rewriteResult.rewritten_message
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {rewriteResult.removed_element && (
                                                <div className="border-t border-white/10 pt-3 mt-3">
                                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                                        What was changed
                                                    </p>
                                                    <p className="text-white/50 font-body text-sm leading-relaxed">
                                                        {rewriteResult.removed_element}
                                                    </p>
                                                </div>
                                            )}

                                            {rewriteResult.intent_preserved && (
                                                <div className="border-t border-white/10 pt-3 mt-3">
                                                    <p className="text-white/40 text-xs font-body uppercase tracking-widest mb-1">
                                                        Intent preserved
                                                    </p>
                                                    <p className="text-white/50 font-body text-sm leading-relaxed">
                                                        {rewriteResult.intent_preserved}
                                                    </p>
                                                </div>
                                            )}

                                            <button
                                                onClick={() => setRewriteResult(null)}
                                                className="text-white/40 text-xs font-body underline mt-3"
                                            >
                                                Hide rewrite
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Analysis Output Disclaimer */}
                        <div className="border border-white/10 bg-white/[0.02] rounded-xl px-5 py-4 mb-6">
                            <p className="text-white/50 text-xs font-body leading-relaxed">
                                This analysis is AI-generated guidance only. GMC does not guarantee
                                outcomes. You are responsible for the message you choose to send.
                            </p>
                        </div>

                        {/* Zero Retention Notice */}
                        <div className="mb-6 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                            <p className="text-white/30 text-xs font-body">
                                Message content was processed in real time and never stored.
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <button
                                onClick={handleReset}
                                className="px-6 py-3 bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] rounded-xl text-sm font-bold tracking-wide transition-all shadow-sm w-full sm:w-auto text-center"
                            >
                                Analyse another message
                            </button>
                            <button
                                onClick={() => navigate('/plans')}
                                className="px-6 py-3 border border-white/10 text-white/70 hover:text-white rounded-xl text-sm font-bold tracking-wide transition-all w-full sm:w-auto text-center"
                            >
                                View plans
                            </button>
                        </div>
                    </>
                )}
            </div>

            <Footer />
        </div>
    )
}